import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import { Mail, ArrowRight, Shield, CreditCard, Clock } from "lucide-react";

function getOrCreateSessionId() {
  let sid = localStorage.getItem("participant_session_id");
  if (!sid) {
    sid = (crypto?.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("participant_session_id", sid);
  }
  return sid;
}

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expiredLogin, setExpiredLogin] = useState(false);   // true → auto-redirect to /pricing

  useEffect(() => {
    if (params.get("expired")) {
      setError("Sesi anda telah tamat atau diberhentikan. Sila log masuk semula.");
    }
  }, [params]);

  // If access is expired at login, auto-redirect to /pricing after 3.5s
  useEffect(() => {
    if (!expiredLogin) return;
    const t = setTimeout(() => {
      navigate(`/pricing?email=${encodeURIComponent(email.trim().toLowerCase())}`);
    }, 3500);
    return () => clearTimeout(t);
  }, [expiredLogin, navigate, email]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setExpiredLogin(false);
    setLoading(true);
    try {
      const session_id = getOrCreateSessionId();
      const { data } = await api.post("/participant/login", {
        email: email.trim().toLowerCase(),
        session_id,
      });
      localStorage.setItem("participant_token", data.token);
      localStorage.setItem("participant_email", data.email);
      navigate("/generator");
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      setError(detail);
      // If the account is expired, escalate: show CTA + auto-redirect to /pricing
      if (/tamat tempoh|expired/i.test(String(detail))) {
        setExpiredLogin(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 grain" />
      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-10">
          <Brand size="lg" />
        </div>

        <div className="bg-[#101010] border border-white/10 rounded-2xl p-8 shadow-2xl shadow-black/60">
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-semibold mb-2">
              Akses Ahli
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">Selamat kembali.</h1>
            <p className="text-sm text-neutral-400">
              Masukkan emel ahli anda. Hanya satu peranti dibenarkan pada satu masa.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="participant-login-form">
            <div>
              <label className="block text-xs text-neutral-500 mb-2 font-mono uppercase tracking-wider">
                Emel
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="anda@faceclub.my"
                  className="w-full pl-11 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-md text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/40 font-mono text-sm transition-all"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3"
                data-testid="login-error"
              >
                {error}
                {expiredLogin && (
                  <div className="mt-3 pt-3 border-t border-red-500/20 flex flex-col gap-2" data-testid="expired-cta">
                    <div className="flex items-center gap-2 text-xs text-amber-300">
                      <Clock size={12} /> Anda akan diarahkan ke halaman langganan…
                    </div>
                    <Link
                      to={`/pricing?email=${encodeURIComponent(email.trim().toLowerCase())}`}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-amber-500 text-black font-semibold text-xs hover:bg-amber-400"
                      data-testid="expired-cta-button"
                    >
                      Sambung Akses Sekarang <ArrowRight size={12} />
                    </Link>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? "Mengesahkan…" : (
                <>
                  Masuk <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="divider-gold my-6" />

          <Link
            to="/pricing"
            className="block w-full text-center px-4 py-3 rounded-md border border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/10 transition-colors text-sm font-semibold mb-3 inline-flex items-center justify-center gap-2"
            data-testid="signup-pricing-link"
          >
            <CreditCard size={14} /> Belum ada akses? Langgan / Beli Pakej
          </Link>

          <Link
            to="/admin/login"
            className="flex items-center justify-center gap-2 text-xs text-neutral-500 hover:text-amber-400 transition-colors"
            data-testid="admin-login-link"
          >
            <Shield size={14} /> Akses Pentadbir
          </Link>
        </div>

        <p className="text-center text-xs text-neutral-600 mt-6 font-mono">
          © Face Club · Karya Tanpa Sempadan
        </p>
      </div>
    </div>
  );
}
